import { makeAutoObservable } from 'mobx';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { OpenapiProxyStaffAffordabilityCheckAuditResponse200Type as ResponseType } from 'src/api_openapi/generated/openapi_proxy_staff_affordability_check_audit';
import { DateTime } from 'src_common/utils/time/time';

type StateType = 'loading' | 'error' | 'finished' | 'noData' | 'initial';

function sortByCheckDate<T extends { checkDate: string }>(a: T, b: T): number {
    if (a.checkDate > b.checkDate) {
        return -1;
    }
    if (a.checkDate < b.checkDate) {
        return 1;
    }
    return 0;
}

const initialFromDate = DateTime.current().subtractHours(48).format('YYYY-MM-DDT00:00:00');
const initialToDate = DateTime.current().format('YYYY-MM-DDT23:59:59');

export class AuditAffordabilityState {
    public readonly affordabilityAuditResource: Resource<void>;

    public auditData: ResponseType = [];
    public stateType: StateType = 'initial';

    public from: string | undefined = initialFromDate;
    public to: string | undefined = initialToDate;

    public constructor(
        private sdkStaff: SdkStaff,
        private routerBackoffice: RouterBackoffice
    ) {
        this.affordabilityAuditResource = new Resource(async (): Promise<void> => {
            this.setStateType('loading');

            const customerId = this.routerBackoffice.getValueString('customers');
            // eslint-disable-next-line no-restricted-globals
            const numberedCustomerId = Number(customerId);

            if (customerId === null || isNaN(numberedCustomerId)) {
                throw new Error('Invalid customerId');
            }

            const response = await this.sdkStaff.postAffordabilityCheckAudit({
                customer_id: numberedCustomerId,
                requestBody: {
                    from: `${this.from}Z`,
                    to: `${this.to}Z`,
                },
            });

            const sortedResponse = [...response].sort(sortByCheckDate);

            this.setAuditData(sortedResponse, response.length === 0 ? 'noData' : 'finished');
        });
        makeAutoObservable(this);
    }

    private setStateType(stateType: StateType): void {
        this.stateType = stateType;
    }

    private setAuditData(auditData: ResponseType, stateType: StateType): void {
        this.auditData = auditData;
        this.stateType = stateType;
    }

    public handleChangeFromDate = async (date: string | null): Promise<void> => {
        if (date === null || date === '') {
            this.from = undefined;
        } else {
            const parsed = DateTime.from(date);
            this.from = parsed?.format('YYYY-MM-DDTHH:mm:ss');
        }

        await this.affordabilityAuditResource.refresh();
    };

    public handleChangetoDate = async (date: string | null): Promise<void> => {
        if (date === null || date === '') {
            this.to = undefined;
        } else {
            const parsed = DateTime.from(date);
            this.to = parsed?.format('YYYY-MM-DDTHH:mm:ss');
        }

        await this.affordabilityAuditResource.refresh();
    };

    public get isModalOpened(): boolean {
        const isOpened = this.routerBackoffice.getParametersString('customers', 'audits') !== null;
        return isOpened;
    }

    public get status(): StateType {
        const requestStatus = this.affordabilityAuditResource.get().type;

        if (requestStatus === 'error') {
            return requestStatus;
        }

        return this.stateType;
    }
}
